import React, { useEffect, useRef } from "react";
import { Check, Plus, X } from "lucide-react";
import SearchableSelectField from "./SearchableSelectField";
import type {
  AcceptCriterion,
  AcceptCriterionRateType,
} from "../utils/acceptCriteriaUtils";
import {
  buildEligibilityRows,
  eligibilityRowKey,
  formatAcceptCriterionRate,
} from "../utils/acceptCriteriaUtils";
import SkillLevelWithInfo from "@/components/skill/SkillLevelWithInfo";

type Option = { value: string; label: string };

type Props = {
  criteria: AcceptCriterion[];
  onChange: (criteria: AcceptCriterion[]) => void;
  specialtyOptions: Option[];
  skillOptions: Option[];
  primarySpecialtyId?: string;
  primarySkillLevelId?: string;
  variant?: "modal" | "form";
  /** When false, only specialty/skill (pay is set on Time & Rate step). */
  showRates?: boolean;
  /** Related roles from catalog — one-click add. */
  suggestions?: AcceptCriterion[];
  /** Broad high-volume specialties — shown when manual add is highlighted. */
  broadSuggestions?: AcceptCriterion[];
  autoOpenManualAdd?: boolean;
  highlightManualAdd?: boolean;
  onAutoOpenManualAddHandled?: () => void;
};

const formSelectClass =
  "min-w-0 flex-1 px-2 py-1.5 border border-gray-200 rounded-md text-sm bg-white focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-400";

const formInputClass =
  "w-24 px-2 py-1.5 border border-gray-200 rounded-md text-sm bg-white focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-400";

const labelFor = (options: Option[], id: string) =>
  options.find((o) => o.value === id)?.label || id;

const RateFields: React.FC<{
  rateType: AcceptCriterionRateType | "";
  rateAmount: string;
  onRateTypeChange: (value: AcceptCriterionRateType | "") => void;
  onRateAmountChange: (value: string) => void;
  compact?: boolean;
}> = ({
  rateType,
  rateAmount,
  onRateTypeChange,
  onRateAmountChange,
  compact = false,
}) => (
  <div className={`flex items-center gap-1.5 ${compact ? "" : "mt-1"}`}>
    <select
      value={rateType}
      onChange={(e) =>
        onRateTypeChange(e.target.value as AcceptCriterionRateType | "")
      }
      className={compact ? formSelectClass : formInputClass}
      style={compact ? { maxWidth: 110 } : undefined}
      aria-label="Rate type"
    >
      <option value="">No rate</option>
      <option value="perHour">$/hr</option>
      <option value="contracted">$/day</option>
    </select>
    {rateType ? (
      <input
        type="number"
        min={0}
        step="any"
        placeholder="Amount"
        value={rateAmount}
        onChange={(e) => onRateAmountChange(e.target.value)}
        className={formInputClass}
        aria-label="Rate amount"
      />
    ) : null}
  </div>
);

const buildRateFields = (
  rateType: AcceptCriterionRateType | "",
  rateAmount: string,
): Pick<AcceptCriterion, "rateType" | "ratePerHour" | "contractedRate"> => {
  if (!rateType || !rateAmount.trim()) {
    return {};
  }
  const amount = Number(rateAmount);
  if (!Number.isFinite(amount) || amount <= 0) {
    return {};
  }
  if (rateType === "perHour") {
    return { rateType, ratePerHour: amount };
  }
  return { rateType, contractedRate: amount };
};

const rateFieldsFromRow = (
  row: AcceptCriterion,
): { rateType: AcceptCriterionRateType | ""; rateAmount: string } => {
  if (row.rateType === "perHour" && row.ratePerHour != null) {
    return { rateType: "perHour", rateAmount: String(row.ratePerHour) };
  }
  if (row.rateType === "contracted" && row.contractedRate != null) {
    return { rateType: "contracted", rateAmount: String(row.contractedRate) };
  }
  return { rateType: "", rateAmount: "" };
};

const SuggestionChips: React.FC<{
  title: string;
  subtitle?: string;
  items: AcceptCriterion[];
  specialtyOptions: Option[];
  skillOptions: Option[];
  onAdd: (row: AcceptCriterion) => void;
  tone?: "related" | "broad";
}> = ({
  title,
  subtitle,
  items,
  specialtyOptions,
  skillOptions,
  onAdd,
  tone = "related",
}) => {
  if (items.length === 0) return null;
  const chipClass =
    tone === "broad"
      ? "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-dashed border-emerald-300 bg-emerald-50/60 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 hover:border-emerald-400 transition-colors"
      : "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-dashed border-indigo-300 bg-white text-xs font-semibold text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 transition-colors";

  return (
    <div className="space-y-1.5">
      <div>
        <p className="text-xs font-medium text-gray-500">{title}</p>
        {subtitle ? (
          <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s) => (
          <button
            key={eligibilityRowKey(s)}
            type="button"
            onClick={() => onAdd(s)}
            className={chipClass}
          >
            <Plus size={13} />
            {labelFor(specialtyOptions, s.specialtyId)} ·{" "}
            {labelFor(skillOptions, s.minSkillLevelId)}
          </button>
        ))}
      </div>
    </div>
  );
};

export const AcceptCriteriaEditor: React.FC<Props> = ({
  criteria,
  onChange,
  specialtyOptions,
  skillOptions,
  primarySpecialtyId,
  primarySkillLevelId,
  variant = "modal",
  showRates = true,
  suggestions = [],
  broadSuggestions = [],
  autoOpenManualAdd = false,
  highlightManualAdd = false,
  onAutoOpenManualAddHandled,
}) => {
  const isModal = variant === "modal";
  const [adding, setAdding] = React.useState(false);
  const [draftSpec, setDraftSpec] = React.useState("");
  const [draftSkill, setDraftSkill] = React.useState("");
  const [draftRateType, setDraftRateType] = React.useState<
    AcceptCriterionRateType | ""
  >("");
  const [draftRateAmount, setDraftRateAmount] = React.useState("");
  const manualAddRef = useRef<HTMLDivElement>(null);

  const eligibilityRows = buildEligibilityRows(
    primarySpecialtyId || "",
    primarySkillLevelId || "",
    criteria,
  );

  const existingKeys = new Set(eligibilityRows.map(eligibilityRowKey));
  const visibleSuggestions = suggestions.filter(
    (s) => !existingKeys.has(eligibilityRowKey(s)),
  );
  const visibleBroadSuggestions = broadSuggestions.filter(
    (s) => !existingKeys.has(eligibilityRowKey(s)),
  );
  const showBroadBucket =
    highlightManualAdd || adding || visibleBroadSuggestions.length > 0;

  const removeRow = (index: number) => {
    onChange(criteria.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, patch: Partial<AcceptCriterion>) => {
    onChange(criteria.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addCriterion = (row: AcceptCriterion) => {
    const key = eligibilityRowKey(row);
    if (existingKeys.has(key)) return;
    onChange([...criteria, row]);
  };

  const openAdd = () => {
    setDraftSpec(primarySpecialtyId || "");
    setDraftSkill(primarySkillLevelId || "");
    setDraftRateType("");
    setDraftRateAmount("");
    setAdding(true);
  };

  const confirmAdd = () => {
    if (!draftSpec || !draftSkill) return;
    addCriterion({
      specialtyId: draftSpec,
      minSkillLevelId: draftSkill,
      ...buildRateFields(draftRateType, draftRateAmount),
    });
    setDraftSpec("");
    setDraftSkill("");
    setDraftRateType("");
    setDraftRateAmount("");
    setAdding(false);
  };

  const cancelAdd = () => {
    setDraftSpec("");
    setDraftSkill("");
    setDraftRateType("");
    setDraftRateAmount("");
    setAdding(false);
  };

  const canAdd = Boolean(primarySpecialtyId && primarySkillLevelId);

  useEffect(() => {
    if (!autoOpenManualAdd || !canAdd) return;
    openAdd();
    onAutoOpenManualAddHandled?.();
    window.setTimeout(() => {
      manualAddRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
  }, [autoOpenManualAdd, canAdd, onAutoOpenManualAddHandled]);

  useEffect(() => {
    if (!highlightManualAdd) return;
    window.setTimeout(() => {
      manualAddRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
  }, [highlightManualAdd]);

  const renderEditableRow = (
    row: ReturnType<typeof buildEligibilityRows>[number],
    index: number,
    compact: boolean,
  ) => {
    const isAnchor = row.isAnchor;
    const criteriaIndex = isAnchor
      ? -1
      : criteria.findIndex(
          (c) => eligibilityRowKey(c) === eligibilityRowKey(row),
        );
    const { rateType, rateAmount } = rateFieldsFromRow(row);
    const rateLabel = formatAcceptCriterionRate(row);

    if (isAnchor) {
      if (compact) {
        return (
          <span
            key={`${row.specialtyId}-${row.minSkillLevelId}-${index}`}
            className="inline-flex items-center gap-1 pl-3 pr-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-sm text-slate-800"
          >
            <span className="font-medium">
              {labelFor(specialtyOptions, row.specialtyId)}
            </span>
            <span className="text-slate-300">·</span>
            <SkillLevelWithInfo
              skillId={row.minSkillLevelId}
              label={labelFor(skillOptions, row.minSkillLevelId)}
              className="inline-flex"
              infoClassName="h-3 w-3"
            />
            <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">
              primary
            </span>
          </span>
        );
      }
      return (
        <li
          key={`${row.specialtyId}-${row.minSkillLevelId}-${index}`}
          className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-100 px-2 py-1.5"
        >
          <span className="text-sm font-medium text-gray-800 min-w-0 flex-1">
            {labelFor(specialtyOptions, row.specialtyId)} ·{" "}
            <SkillLevelWithInfo
              skillId={row.minSkillLevelId}
              label={labelFor(skillOptions, row.minSkillLevelId)}
              className="inline-flex"
              infoClassName="h-3 w-3"
            />
          </span>
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            primary
          </span>
        </li>
      );
    }

    if (compact) {
      return (
        <div
          key={`${row.specialtyId}-${row.minSkillLevelId}-${index}`}
          className="flex flex-wrap items-center gap-2 rounded-xl bg-indigo-50/70 border border-indigo-200 px-2 py-2 animate-popIn"
          style={{ animationDelay: `${index * 40}ms` }}
        >
          <div className="flex-1 min-w-[140px]">
            <SearchableSelectField
              compact
              value={row.specialtyId}
              onChange={(specialtyId) =>
                updateRow(criteriaIndex, { specialtyId })
              }
              options={specialtyOptions}
              placeholder="Specialty…"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <SearchableSelectField
              compact
              value={row.minSkillLevelId}
              onChange={(minSkillLevelId) =>
                updateRow(criteriaIndex, { minSkillLevelId })
              }
              options={skillOptions}
              placeholder="Min skill…"
            />
          </div>
          {showRates ? (
            <RateFields
              compact
              rateType={rateType}
              rateAmount={rateAmount}
              onRateTypeChange={(value) => {
                if (!value) {
                  updateRow(criteriaIndex, {
                    rateType: undefined,
                    ratePerHour: undefined,
                    contractedRate: undefined,
                  });
                  return;
                }
                updateRow(criteriaIndex, {
                  ...buildRateFields(value, rateAmount),
                });
              }}
              onRateAmountChange={(value) =>
                updateRow(criteriaIndex, {
                  ...buildRateFields(rateType, value),
                })
              }
            />
          ) : rateLabel ? (
            <span className="text-xs font-semibold text-indigo-700">
              {rateLabel}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => removeRow(criteriaIndex)}
            className="shrink-0 p-1.5 rounded-full text-indigo-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            aria-label="Remove"
          >
            <X size={14} />
          </button>
        </div>
      );
    }

    return (
      <li
        key={`${row.specialtyId}-${row.minSkillLevelId}-${index}`}
        className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5"
      >
        <select
          value={row.specialtyId}
          onChange={(e) =>
            updateRow(criteriaIndex, { specialtyId: e.target.value })
          }
          className={formSelectClass}
        >
          <option value="">Specialty…</option>
          {specialtyOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={row.minSkillLevelId}
          onChange={(e) =>
            updateRow(criteriaIndex, { minSkillLevelId: e.target.value })
          }
          className={formSelectClass}
        >
          <option value="">Skill…</option>
          {skillOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {!isAnchor && (
          <RateFields
            compact
            rateType={rateType}
            rateAmount={rateAmount}
            onRateTypeChange={(value) => {
              if (!value) {
                updateRow(criteriaIndex, {
                  rateType: undefined,
                  ratePerHour: undefined,
                  contractedRate: undefined,
                });
                return;
              }
              updateRow(criteriaIndex, {
                ...buildRateFields(value, rateAmount),
              });
            }}
            onRateAmountChange={(value) =>
              updateRow(criteriaIndex, {
                ...buildRateFields(rateType, value),
              })
            }
          />
        )}
        <button
          type="button"
          onClick={() => removeRow(criteriaIndex)}
          className="shrink-0 p-1 text-gray-400 hover:text-red-500"
        >
          <X size={15} />
        </button>
      </li>
    );
  };

  if (!isModal) {
    return (
      <div className="space-y-2 pt-1">
        <div>
          <label className="text-sm font-semibold text-gray-700">
            Eligible roles
          </label>
          <p className="text-xs text-gray-500 mt-0.5">
            Optional pay rate per role (uses shift day rate when blank).
          </p>
        </div>
        {eligibilityRows.length > 0 && (
          <ul className="space-y-2">
            {eligibilityRows.map((row, index) => renderEditableRow(row, index, false))}
          </ul>
        )}
        <SuggestionChips
          title="Suggested roles"
          items={visibleSuggestions}
          specialtyOptions={specialtyOptions}
          skillOptions={skillOptions}
          onAdd={addCriterion}
        />
        {showBroadBucket ? (
          <SuggestionChips
            title="Broad specialties"
            subtitle="High shift volume — tap to add quickly"
            items={visibleBroadSuggestions}
            specialtyOptions={specialtyOptions}
            skillOptions={skillOptions}
            onAdd={addCriterion}
            tone="broad"
          />
        ) : null}
        <button
          type="button"
          onClick={openAdd}
          disabled={!canAdd}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-300"
        >
          + Add eligible role
        </button>
        {adding ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <select
              value={draftSpec}
              onChange={(e) => setDraftSpec(e.target.value)}
              className={formSelectClass}
            >
              <option value="">Specialty…</option>
              {specialtyOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={draftSkill}
              onChange={(e) => setDraftSkill(e.target.value)}
              className={formSelectClass}
            >
              <option value="">Skill…</option>
              {skillOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <RateFields
              compact
              rateType={draftRateType}
              rateAmount={draftRateAmount}
              onRateTypeChange={setDraftRateType}
              onRateAmountChange={setDraftRateAmount}
            />
            <button type="button" onClick={confirmAdd} className="text-xs text-indigo-600">
              Add
            </button>
            <button type="button" onClick={cancelAdd} className="text-xs text-gray-500">
              Cancel
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          Eligible roles
          <span className="text-xs font-normal text-gray-400">optional extras</span>
        </label>
        {primarySpecialtyId && primarySkillLevelId ? (
          <p className="text-xs text-gray-500 mt-1">
            Doctors matching any listed role can apply. Tap suggestions or use
            Add eligible role to broaden who can see this shift.
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">
            Set specialty and skill level first.
          </p>
        )}
      </div>

      {eligibilityRows.length > 0 && (
        <div className="space-y-2">
          {eligibilityRows.map((row, index) => renderEditableRow(row, index, true))}
        </div>
      )}

      {visibleSuggestions.length > 0 && (
        <SuggestionChips
          title="Suggested roles"
          subtitle="Often selected alongside your primary role"
          items={visibleSuggestions}
          specialtyOptions={specialtyOptions}
          skillOptions={skillOptions}
          onAdd={addCriterion}
        />
      )}

      {highlightManualAdd && visibleSuggestions.length === 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          No related suggestions for this specialty — add broader eligible roles
          below, or click Continue again to skip.
        </div>
      ) : null}

      {showBroadBucket && visibleBroadSuggestions.length > 0 ? (
        <SuggestionChips
          title="Broad specialties"
          subtitle="High shift volume on StatDoctor — good when you want a wider pool"
          items={visibleBroadSuggestions}
          specialtyOptions={specialtyOptions}
          skillOptions={skillOptions}
          onAdd={addCriterion}
          tone="broad"
        />
      ) : null}

      <div
        ref={manualAddRef}
        className={
          highlightManualAdd
            ? "rounded-2xl border-2 border-indigo-300 bg-indigo-50/40 p-3 shadow-sm ring-4 ring-indigo-100/70"
            : undefined
        }
      >
        {highlightManualAdd ? (
          <p className="text-xs font-semibold text-indigo-800 mb-2">
            Add who else can apply — search any specialty and skill level
          </p>
        ) : null}
        {adding ? (
          <div className="space-y-2 animate-slideDown">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex-1 min-w-[160px]">
                <SearchableSelectField
                  compact
                  value={draftSpec}
                  onChange={setDraftSpec}
                  options={specialtyOptions}
                  placeholder="Specialty…"
                />
              </div>
              <div className="flex-1 min-w-[160px] flex items-center gap-1">
                <div className="flex-1 min-w-0">
                  <SearchableSelectField
                    compact
                    value={draftSkill}
                    onChange={setDraftSkill}
                    options={skillOptions}
                    placeholder="Min skill…"
                  />
                </div>
                <SkillLevelWithInfo infoOnly infoClassName="h-3.5 w-3.5" />
              </div>
              <button
                type="button"
                onClick={confirmAdd}
                disabled={!draftSpec || !draftSkill}
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-all hover:scale-105 active:scale-95 shadow-md shadow-indigo-200"
                aria-label="Add"
              >
                <Check size={18} />
              </button>
              <button
                type="button"
                onClick={cancelAdd}
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
                aria-label="Cancel"
              >
                <X size={18} />
              </button>
            </div>
            {showRates ? (
              <RateFields
                rateType={draftRateType}
                rateAmount={draftRateAmount}
                onRateTypeChange={setDraftRateType}
                onRateAmountChange={setDraftRateAmount}
              />
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={openAdd}
            disabled={!canAdd}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Plus size={16} />
            Add eligible role
          </button>
        )}
      </div>
    </div>
  );
};

export default AcceptCriteriaEditor;
